/**
 * Open a site in team review inside the signed-in account: the destination of
 * the free audit's Sign in link (`/open-site?site=`, OpenSitePage.tsx).
 *
 * Emily, 2026-11-02 ("Open site, no move"): nothing moves from the free session.
 * The claim service refuses a free audit by design (it has no comments to prove
 * the visitor wrote it, freeToolClaim.service.ts boundReviewerId) and the audit's
 * findings are never saved with the review, so the honest destination is the
 * same site in the visitor's own workspace: their project for it, or a new one
 * that knows the site, then a team review with Auto Review's panel open to run.
 *
 * Pure apart from the injected calls, the shape of lib/onboardingFinish.ts.
 */
import { buildCreatedReviewUrl } from '@/lib/createdReviewUrl';
import { deriveProjectSlug } from '@/lib/projectSlug';
import { resolveProjectName } from '@/components/onboarding/StepProject';
import { log } from '@/lib/logger';
import { newUuid, type AuthBootstrapResponse, type Project } from '@overlayqa/shared';
import type { PendingExtensionOpen } from './pendingExtensionOpen';

export interface OpenSiteProject {
  id: string;
  role?: string | null;
  archivedAt?: string | null;
  isDemo?: boolean | null;
  primaryDomain?: string | null;
  reviewEntryUrl?: string | null;
}

export interface OpenSiteDeps {
  projects: OpenSiteProject[];
  teamId: string | null;
  extensionDetected: boolean;
  /** A project an earlier attempt already created, so a retry never makes a second one. */
  knownProjectId?: string | null;
  createProject: (input: { teamId: string; name: string; slug: string; primaryDomain: string; reviewEntryUrl: string }) => Promise<{ id: string }>;
  createLiveReview: (input: { projectId: string; pageUrl: string }) => Promise<{ shareUrl?: string | null; liveAvailable?: boolean; authHandoff?: string | null }>;
}

export interface OpenSiteResult {
  url: string;
  project: 'existing' | 'created';
  projectId: string;
}

/** Which step failed, and the project that exists by then (null when none was found or made). */
export class OpenSiteError extends Error {
  constructor(message: string, readonly stage: 'project' | 'review', readonly projectId: string | null) {
    super(message);
    this.name = 'OpenSiteError';
  }
}

/** Roles that can open a review on a project (the FramerReviewPage set). */
const EDITABLE_ROLES = ['owner', 'collaborator', 'team'];

/** Recovery stays on the exact project returned by its authorized GET, never a host match. */
export function extensionRecoveryHandoff(site: string, requestedProjectId: string,
  project: (Pick<Project, 'id' | 'teamId' | 'name' | 'slug' | 'role'> & { archivedAt?: string | Date | null }) | undefined,
  session: AuthBootstrapResponse): PendingExtensionOpen {
  if (!requestedProjectId || !project || project.id !== requestedProjectId) {
    throw new Error('This project is not available. Open the review from a project you can access.');
  }
  if (project.archivedAt || !EDITABLE_ROLES.includes(project.role ?? '')) {
    throw new Error('You need capture access to an active project to open it in the extension.');
  }
  if (project.teamId !== session.activeTeamId && !session.teams?.some(team => team.id === project.teamId)) {
    throw new Error('This project is not available in your account. Sign in to an account with access.');
  }
  return { url: site, urlType: 'review_recovery', source: 'team_review', handoffId: newUuid(), projectId: project.id,
    projectName: project.name, projectSlug: project.slug, teamId: project.teamId, userId: session.user.id };
}

function hostKey(raw: string): string | null {
  try {
    return new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** The user's live, editable project for this site's host, if any. */
export function findSiteProject(projects: OpenSiteProject[], site: string): OpenSiteProject | undefined {
  const host = hostKey(site);
  if (!host) return undefined;
  return projects.find(p => !p.archivedAt && !p.isDemo && EDITABLE_ROLES.includes(p.role ?? '')
    && [p.primaryDomain, p.reviewEntryUrl].some(value => !!value && hostKey(value) === host));
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Something went wrong.';
}

export async function openSite(site: string, deps: OpenSiteDeps): Promise<OpenSiteResult> {
  const existing = deps.knownProjectId ? { id: deps.knownProjectId } : findSiteProject(deps.projects, site);
  let projectId = existing?.id ?? null;
  if (!projectId) {
    if (!deps.teamId) throw new OpenSiteError('No workspace is selected.', 'project', null);
    const name = resolveProjectName('', site) ?? new URL(site).hostname;
    try {
      const created = await deps.createProject({ teamId: deps.teamId, name, slug: deriveProjectSlug(name), primaryDomain: new URL(site).hostname, reviewEntryUrl: site });
      projectId = created.id;
    } catch (error) {
      log.warn('[openSite] project create failed', { error: messageOf(error) });
      throw new OpenSiteError(messageOf(error), 'project', null);
    }
  }
  try {
    const review = await deps.createLiveReview({ projectId, pageUrl: site });
    const url = buildCreatedReviewUrl({
      shareUrl: review.shareUrl,
      liveAvailable: review.liveAvailable,
      extensionDetected: deps.extensionDetected,
      authHandoff: review.authHandoff,
      openWorkflow: 'auto-review',
    });
    if (!url) throw new Error('The review was created without a link. Try again.');
    log.info('[openSite] review ready', { projectId, project: existing ? 'existing' : 'created' });
    return { url, project: existing ? 'existing' : 'created', projectId };
  } catch (error) {
    log.warn('[openSite] review create failed', { projectId, error: messageOf(error) });
    throw new OpenSiteError(messageOf(error), 'review', projectId);
  }
}
